import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileSignature, Upload, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

export const DOC_KINDS = [["buyer_agency", "Buyer agency agreement"], ["closing_credit", "Closing-credit (rebate) disclosure"], ["other", "Other document"]];

export default function DocumentTemplates() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("buyer_agency");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/esign/templates").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("name", name || file.name.replace(/\.pdf$/i, "")); fd.append("kind", kind);
      await api.post("/esign/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template uploaded"); setName(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (t) => { if (!window.confirm(`Delete template "${t.name}"? Already-sent documents keep working.`)) return; await api.delete(`/esign/templates/${t.id}`); load(); };
  return (
    <section className="border border-border rounded-md bg-card" data-testid="esign-templates">
      <div className="p-5 border-b border-border">
        <div className="text-sm font-medium flex items-center gap-2"><FileSignature className="w-4 h-4 text-muted-foreground" />E-sign document templates</div>
        <div className="text-xs text-muted-foreground">Upload your buyer agency agreement and closing-credit disclosure as PDFs. Send them from any deal; buyers sign by typing or drawing, and the PDF is stamped with signature + audit trail (email, time, IP).</div>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div className="space-y-1.5"><Label>Template name</Label><Input data-testid="esign-tpl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Buyer Agency Agreement (PA)" className="h-8 text-xs" /></div>
          <div className="space-y-1.5"><Label>Type</Label><select data-testid="esign-tpl-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 w-full text-xs rounded-md border border-border bg-background px-2">{DOC_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          <div><input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="esign-tpl-file" onChange={(e) => upload(e.target.files?.[0])} /><Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="esign-tpl-upload"><Upload className="w-3.5 h-3.5 mr-1.5" />{busy ? "Uploading…" : "Upload PDF"}</Button></div>
        </div>
        <div className="space-y-1.5">
          {items.length === 0 && <div className="text-xs text-muted-foreground" data-testid="esign-tpl-empty">No templates yet.</div>}
          {items.map((t) => (
            <div key={t.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2 text-sm" data-testid={`esign-tpl-${t.id}`}>
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1"><div className="font-medium truncate">{t.name}</div><div className="text-[11px] text-muted-foreground">{t.kind_label} · {t.pages} page{t.pages === 1 ? "" : "s"} · {Math.round((t.size || 0) / 1024)} KB</div></div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(t)} data-testid={`esign-tpl-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
